import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type TextareaHTMLAttributes
} from 'react';
import { clsx } from 'clsx';
import { useNutzer, type Nutzer } from '@/hooks/useNutzer';
import { FARBE_NUTZER_FALLBACK } from '@/theme';

// Textfeld mit @-Vorschlagsliste.
//
// Die Liste hilft beim Tippen — mehr nicht. Wer erwähnt wurde, entscheidet
// beim Speichern der Server, indem er denselben Text noch einmal liest.
// Die Oberfläche kann also nichts „vergessen“ und nichts hinzudichten.
//
// Touch zuerst: die Zeilen sind 44 Pixel hoch und werden per Tippen
// gewählt. Pfeiltasten und Enter gibt es zusätzlich für den Bürorechner,
// nicht anstelle des Tippens.

/** Was zwischen dem letzten "@" und dem Schreibcursor steht. */
const OFFENE_ERWAEHNUNG = /@([\p{L}\p{N}]*)$/u;

type Props = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> & {
  value: string;
  onChange: (wert: string) => void;
};

export function ErwaehnungTextarea({ value, onChange, onKeyDown, className, ...rest }: Props) {
  const nutzer = useNutzer();
  const feld = useRef<HTMLTextAreaElement>(null);
  // null = keine offene Erwähnung. Sonst die Position des "@" im Text.
  const [atPos, setAtPos] = useState<number | null>(null);
  // Welches "@" hat der Mitarbeiter mit Escape weggeschickt? Ohne dieses
  // Gedächtnis kommt die Liste sofort zurück: Escape löst auch ein
  // keyUp aus, und dort steht immer noch dasselbe "@MK" vor dem Cursor.
  const verworfenBei = useRef<number | null>(null);
  // Wohin der Schreibcursor nach dem Einsetzen gehört. Siehe unten.
  const [cursorZiel, setCursorZiel] = useState<number | null>(null);
  const [praefix, setPraefix] = useState('');
  const [markiert, setMarkiert] = useState(0);

  const alle = nutzer.data?.nutzer ?? [];
  const p = praefix.toLocaleLowerCase('de-DE');
  const treffer =
    atPos === null
      ? []
      : alle.filter(
          (n) =>
            n.kuerzel.toLocaleLowerCase('de-DE').startsWith(p) ||
            n.name.toLocaleLowerCase('de-DE').includes(p)
        );
  const offen = treffer.length > 0;

  /**
   * Die Markierung gehört genau dann zurück nach oben, wenn sich die
   * Trefferliste WIRKLICH ändert.
   *
   * Vorher stand das Zurücksetzen in pruefeCursor() — und das läuft bei
   * jedem keyUp. Die Pfeiltaste bewegte die Markierung im keydown
   * korrekt, das keyUp derselben Taste holte sie sofort wieder auf 0.
   * Mit den Pfeiltasten war damit niemand unterhalb des ersten Eintrags
   * erreichbar. `preventDefault()` half nicht: es unterdrückt die
   * Cursorbewegung, nicht das keyUp.
   *
   * Deshalb hängt der Rücksprung jetzt an den Treffern selbst und nicht
   * mehr am Tastendruck. Wer weitertippt, ohne dass jemand herausfällt,
   * behält seine Markierung; wer die Liste einengt, fängt oben an — dort
   * steht sonst jemand markiert, den es nicht mehr gibt.
   */
  const trefferSchluessel = treffer.map((n) => n.id).join(',');
  const letzteTreffer = useRef(trefferSchluessel);
  useEffect(() => {
    if (letzteTreffer.current === trefferSchluessel) return;
    letzteTreffer.current = trefferSchluessel;
    setMarkiert(0);
  }, [trefferSchluessel]);

  /**
   * Der Index, mit dem gerechnet und gerendert wird.
   *
   * Zwischen dem Bild, in dem die Liste kürzer wird, und dem Effekt oben
   * liegt ein Moment, in dem `markiert` über das Ende hinauszeigt. Ohne
   * diese Klammer stünde in genau diesem Moment `treffer[markiert] ===
   * undefined` — und ein Enter darauf hätte den Erwähnungs-Einsatz
   * zerlegt.
   */
  const markiertSicher = treffer.length === 0 ? 0 : Math.min(markiert, treffer.length - 1);

  function pruefeCursor(text: string, cursor: number): void {
    const treffer = OFFENE_ERWAEHNUNG.exec(text.slice(0, cursor));
    if (treffer === null) {
      verworfenBei.current = null;
      setAtPos(null);
      return;
    }
    const start = cursor - treffer[0].length;
    if (verworfenBei.current === start) return;
    verworfenBei.current = null;
    setAtPos(start);
    setPraefix(treffer[1]);
  }

  function uebernehmen(n: Nutzer): void {
    if (atPos === null) return;
    const el = feld.current;
    const cursor = el?.selectionStart ?? value.length;
    // Ein Leerzeichen hinterher: sonst tippt man direkt weiter und macht
    // aus "@MK" ein "@MKbitte", das der Server nicht mehr als Erwähnung
    // erkennt (die Abgrenzung nach hinten ist Absicht).
    const einsetzen = `@${n.kuerzel} `;
    onChange(value.slice(0, atPos) + einsetzen + value.slice(cursor));
    setAtPos(null);
    setCursorZiel(atPos + einsetzen.length);
  }

  /**
   * Setzt den Schreibcursor hinter das Eingesetzte.
   *
   * Nötig, weil der Cursor beim Austausch des Feldinhalts sonst dort
   * stehenbleibt, wo er beim Tippen war — mitten im Text.
   *
   * Und zwar als Layout-Effekt, NICHT über requestAnimationFrame. Der
   * Unterschied ist im laufenden Build aufgefallen: wer sofort nach der
   * Auswahl weitertippt, dessen erstes Zeichen kommt an, bevor das
   * nächste Bild gezeichnet ist — und der Nachzügler aus dem rAF schiebt
   * den Cursor dann HINTER dieses Zeichen zurück. Aus „@MK bitte …" wurde
   * so „@MK itte …b". Der Layout-Effekt läuft direkt nach dem
   * DOM-Update und vor dem nächsten Tastendruck.
   */
  useLayoutEffect(() => {
    if (cursorZiel === null) return;
    const el = feld.current;
    el?.focus();
    el?.setSelectionRange(cursorZiel, cursorZiel);
    setCursorZiel(null);
  }, [cursorZiel, value]);

  function tastendruck(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (offen) {
      // Gerechnet wird vom geklammerten Index aus, nicht vom rohen —
      // sonst rechnet ein Pfeildruck unmittelbar nach dem Kürzerwerden
      // der Liste von einer Zahl aus, die es nicht mehr gibt. Umlaufen
      // statt anhalten: so war es hier schon, und so bleibt es.
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMarkiert((markiertSicher + 1) % treffer.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMarkiert((markiertSicher - 1 + treffer.length) % treffer.length);
        return;
      }
      // Enter und Tab wählen aus. Das muss VOR dem eigenen Handler des
      // Formulars passieren, sonst schickt Strg+Enter den halb getippten
      // Eintrag ab, während die Liste noch offen steht.
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        uebernehmen(treffer[markiertSicher]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        verworfenBei.current = atPos;
        setAtPos(null);
        return;
      }
    }
    onKeyDown?.(e);
  }

  return (
    <div className="relative">
      <textarea
        {...rest}
        ref={feld}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          pruefeCursor(e.target.value, e.target.selectionStart);
        }}
        onKeyUp={(e) => {
          // Auch reines Cursor-Bewegen kann in eine Erwähnung hinein- oder
          // aus ihr herausführen.
          const el = e.currentTarget;
          pruefeCursor(el.value, el.selectionStart);
        }}
        onBlur={() => setAtPos(null)}
        onKeyDown={tastendruck}
        className={className}
      />

      {offen && (
        <ul
          // onMouseDown statt onClick am Eintrag: onBlur des Feldes feuert
          // zuerst und würde die Liste wegnehmen, ehe der Klick ankommt.
          className="absolute left-0 top-full z-20 mt-1 max-h-56 w-64 overflow-y-auto rounded-md border border-line bg-cream py-1 shadow-sm"
          role="listbox"
          aria-label="Mitarbeiter erwähnen"
        >
          {treffer.map((n, i) => (
            <li key={n.id}>
              <button
                type="button"
                role="option"
                aria-selected={i === markiertSicher}
                onMouseDown={(e) => {
                  e.preventDefault();
                  uebernehmen(n);
                }}
                className={clsx(
                  'flex min-h-[44px] w-full items-center gap-2 px-3 text-left text-sm',
                  i === markiertSicher ? 'bg-akzent-sanft text-akzent-stark' : 'text-ink hover:bg-cream-dark'
                )}
              >
                <span
                  aria-hidden
                  className="inline-block h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: n.farbe ?? FARBE_NUTZER_FALLBACK }}
                />
                <span className="font-medium">{n.kuerzel}</span>
                {/* Der Name steht bewusst daneben: zwei Mitarbeiter können
                    dasselbe Kürzel tragen, und dann ist "@MK" für beide.
                    Wer die Doppelung hier sieht, schreibt den Namen dazu. */}
                <span className="truncate text-ink-faint">{n.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
